import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AppLayout from '../layout/AppLayout';
import { serverService, hostedAppService, userService, whatsappAccountService } from '../../services/api';
import { INK, MUTED, RULE, FigureStrip, formatCount, formatINR } from '../analytics/analyticsUi';

const GOOD = '#0D7332';
const BAD = '#C2410C';
const buttonSx = { textTransform: 'none' };
const money = (value) => (value === null || value === undefined ? '–' : formatINR(value));
const marginColor = (value) => (value >= 0 ? GOOD : BAD);

const PURPOSES = [
  { value: 'client-hosting', label: 'Client hosting' },
  { value: 'bots', label: 'Bots' },
  { value: 'calls', label: 'WhatsApp calls' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'vpn', label: 'VPN' },
  { value: 'portal', label: 'This portal' },
  { value: 'other', label: 'Other' }
];
const purposeLabel = (value) => (PURPOSES.find((p) => p.value === value) || PURPOSES[0]).label;

const EMPTY_SERVER = { ipAddress: '', hostname: '', provider: 'OVH', spec: '', monthlyCost: '', purpose: 'client-hosting', status: 'active', notes: '' };
const EMPTY_APP = { clientName: '', domain: '', port: '', databaseName: '', apiUrl: '', webhookUrl: '', user: '', whatsappAccount: '', status: 'active', notes: '' };

// ---------------------------------------------------------------------------
// Add or edit a server
// ---------------------------------------------------------------------------
const ServerDialog = ({ open, server, onClose, onSaved, onError }) => {
  const [form, setForm] = useState(EMPTY_SERVER);
  const [saving, setSaving] = useState(false);
  const editing = !!server?._id;

  useEffect(() => {
    if (!open) return;
    setForm(server ? { ...EMPTY_SERVER, ...server, monthlyCost: server.monthlyCost ?? '' } : EMPTY_SERVER);
  }, [open, server]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    try {
      setSaving(true);
      const body = { ...form, monthlyCost: Number(form.monthlyCost) || 0 };
      const response = editing ? await serverService.update(server._id, body) : await serverService.create(body);
      onSaved(response.data, editing);
    } catch (err) {
      onError(err.response?.data?.error || 'The server could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: INK, fontWeight: 600 }}>{editing ? 'Edit server' : 'Add a server'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 0.5 }}>
          <TextField label="IP address" size="small" required value={form.ipAddress} onChange={(e) => update({ ipAddress: e.target.value })} helperText="How we refer to the server, e.g. 139.99.8.9" />
          <TextField label="Hostname" size="small" value={form.hostname || ''} onChange={(e) => update({ hostname: e.target.value })} helperText="Optional, e.g. n2069.cloud.bitvoice.in" />
          <TextField label="Provider" size="small" value={form.provider || ''} onChange={(e) => update({ provider: e.target.value })} />
          <TextField
            label="Monthly cost"
            size="small"
            type="number"
            value={form.monthlyCost}
            onChange={(e) => update({ monthlyCost: e.target.value })}
            helperText="Rupees per month, before tax"
          />
          <TextField label="Specification" size="small" value={form.spec || ''} onChange={(e) => update({ spec: e.target.value })} helperText="Optional, e.g. 62 GB RAM" />
          <FormControl size="small">
            <InputLabel id="server-purpose">Used for</InputLabel>
            <Select labelId="server-purpose" label="Used for" value={form.purpose || 'client-hosting'} onChange={(e) => update({ purpose: e.target.value })}>
              {PURPOSES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="server-status">Status</InputLabel>
            <Select labelId="server-status" label="Status" value={form.status} onChange={(e) => update({ status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="retired">Retired</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Notes" size="small" multiline minRows={2} value={form.notes || ''} onChange={(e) => update({ notes: e.target.value })} sx={{ gridColumn: '1 / -1' }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={buttonSx}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !form.ipAddress.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ ...buttonSx, fontWeight: 600 }}
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add server'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Add or edit one client's install
// ---------------------------------------------------------------------------
const AppDialog = ({ open, app, serverId, customers, accounts, onClose, onSaved, onError }) => {
  const [form, setForm] = useState(EMPTY_APP);
  const [saving, setSaving] = useState(false);
  const editing = !!app?._id;

  useEffect(() => {
    if (!open) return;
    setForm(app
      ? {
        ...EMPTY_APP,
        ...app,
        port: app.port ?? '',
        user: app.user?._id || app.user || '',
        whatsappAccount: app.whatsappAccount?._id || app.whatsappAccount || ''
      }
      : EMPTY_APP);
  }, [open, app]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    try {
      setSaving(true);
      const body = {
        ...form,
        server: serverId,
        port: form.port === '' ? undefined : Number(form.port),
        user: form.user || null,
        whatsappAccount: form.whatsappAccount || null
      };
      const response = editing ? await hostedAppService.update(app._id, body) : await hostedAppService.create(body);
      onSaved(response.data, editing);
    } catch (err) {
      onError(err.response?.data?.error || 'The app could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: INK, fontWeight: 600 }}>{editing ? 'Edit hosted app' : 'Add a hosted app'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 0.5 }}>
          <TextField label="Client name" size="small" required value={form.clientName} onChange={(e) => update({ clientName: e.target.value })} />
          <TextField label="Domain" size="small" required value={form.domain} onChange={(e) => update({ domain: e.target.value })} helperText="What the customer opens" />
          <TextField label="Port" size="small" type="number" value={form.port} onChange={(e) => update({ port: e.target.value })} />
          <TextField label="Database name" size="small" value={form.databaseName || ''} onChange={(e) => update({ databaseName: e.target.value })} />
          <TextField label="API URL" size="small" value={form.apiUrl || ''} onChange={(e) => update({ apiUrl: e.target.value })} sx={{ gridColumn: { sm: '1 / -1' } }} />
          <TextField label="Webhook URL" size="small" value={form.webhookUrl || ''} onChange={(e) => update({ webhookUrl: e.target.value })} sx={{ gridColumn: { sm: '1 / -1' } }} />
          {/* Both lists run to a couple of hundred entries, so they are typed into rather than scrolled */}
          <Autocomplete
            size="small"
            options={customers}
            value={customers.find((c) => String(c._id) === String(form.user)) || null}
            onChange={(e, option) => update({ user: option ? option._id : '' })}
            getOptionLabel={(c) => c.company || c.name || ''}
            isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
            renderOption={(props, c) => (
              <Box component="li" {...props} key={c._id}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: INK }} noWrap>{c.company || c.name}</Typography>
                  {c.company && c.name && <Typography variant="caption" sx={{ color: MUTED }} noWrap>{c.name}</Typography>}
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Customer" placeholder="Search by name" helperText="Leave empty until you know which customer this is" />}
          />
          <Autocomplete
            size="small"
            options={accounts}
            value={accounts.find((a) => String(a._id) === String(form.whatsappAccount)) || null}
            onChange={(e, option) => update({ whatsappAccount: option ? option._id : '' })}
            getOptionLabel={(a) => `${a.verifiedName || a.businessName || ''} ${a.phoneNumber || ''}`.trim()}
            isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
            renderOption={(props, a) => (
              <Box component="li" {...props} key={a._id}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: INK }} noWrap>{a.verifiedName || a.businessName}</Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>{a.phoneNumber}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="WhatsApp number" placeholder="Search name or number" helperText="Optional" />}
          />
          <FormControl size="small">
            <InputLabel id="app-status">Status</InputLabel>
            <Select labelId="app-status" label="Status" value={form.status} onChange={(e) => update({ status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Notes" size="small" value={form.notes || ''} onChange={(e) => update({ notes: e.target.value })} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={buttonSx}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !form.clientName.trim() || !form.domain.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ ...buttonSx, fontWeight: 600 }}
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add app'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RowMenu = ({ onEdit, onDelete, label }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={label}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem onClick={() => { close(); onEdit(); }}><ListItemText primary="Edit" /></MenuItem>
        <MenuItem onClick={() => { close(); onDelete(); }}><ListItemText primary="Delete" primaryTypographyProps={{ color: 'error' }} /></MenuItem>
      </Menu>
    </>
  );
};

// ---------------------------------------------------------------------------
// One server: its apps
// ---------------------------------------------------------------------------
const ServerDetail = ({ server, customers, accounts, onBack, onChanged, onError, onSuccess }) => {
  const [detail, setDetail] = useState(null);
  const [appDialog, setAppDialog] = useState({ open: false, app: null });

  const load = useCallback(() => {
    serverService.get(server._id)
      .then((response) => setDetail(response.data))
      .catch(() => onError('The server could not be loaded.'));
  }, [server._id, onError]);

  useEffect(load, [load]);

  const removeApp = async (app) => {
    try {
      await hostedAppService.remove(app._id);
      onSuccess(`${app.clientName} removed from this server.`);
      load();
      onChanged();
    } catch (err) {
      onError(err.response?.data?.error || 'The app could not be deleted.');
    }
  };

  const apps = detail?.apps || [];

  return (
    <>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ...buttonSx, mb: 2 }}>All servers</Button>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 700, color: INK, letterSpacing: '-0.02em' }}>
            {server.ipAddress}
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
            {[server.hostname, server.provider, server.spec, purposeLabel(server.purpose)].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAppDialog({ open: true, app: null })} sx={buttonSx}>
          Add a hosted app
        </Button>
      </Box>

      {detail && (
        <FigureStrip
          figures={[
            { label: 'Clients', value: formatCount(detail.clients) },
            { label: 'Monthly cost', value: money(detail.monthlyCost) },
            { label: 'Cost per client', value: money(detail.costPerClient) },
            { label: 'Plan revenue', value: money(detail.planRevenue), hint: `${detail.linkedClients} linked to a customer` }
          ]}
        />
      )}

      <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden', mt: 3 }}>
        {!detail ? (
          <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={200} /></Box>
        ) : !apps.length ? (
          <Box sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No clients on this server yet</Typography>
            <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch' }}>
              Add each client install with its domain, so the cost per client is worked out and the customer can open their app.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  {['Client', 'Domain', 'Port', 'Database', 'Linked to', ''].map((label, i) => (
                    <TableCell key={label || 'menu'} sx={{ color: MUTED, fontWeight: 500, borderColor: RULE, whiteSpace: 'nowrap', pl: i === 0 ? { xs: 2, sm: 3 } : undefined }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app._id} hover sx={{ '& td': { borderColor: RULE } }}>
                    <TableCell sx={{ pl: { xs: 2, sm: 3 }, color: INK }}>
                      {app.clientName}
                      {app.status === 'suspended' && <Chip size="small" label="Suspended" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="a"
                        href={`https://${app.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {app.domain}<OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{app.port || '–'}</TableCell>
                    <TableCell sx={{ color: MUTED }}>{app.databaseName || '–'}</TableCell>
                    <TableCell sx={{ color: app.user ? INK : MUTED }}>
                      {app.user ? (app.user.company || app.user.name) : 'Not linked yet'}
                      {app.whatsappAccount && (
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{app.whatsappAccount.phoneNumber}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ pr: { xs: 2, sm: 3 }, width: 48 }}>
                      <RowMenu
                        label={`Actions for ${app.clientName}`}
                        onEdit={() => setAppDialog({ open: true, app })}
                        onDelete={() => removeApp(app)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <AppDialog
        open={appDialog.open}
        app={appDialog.app}
        serverId={server._id}
        customers={customers}
        accounts={accounts}
        onClose={() => setAppDialog({ open: false, app: null })}
        onSaved={(_, editing) => {
          setAppDialog({ open: false, app: null });
          onSuccess(editing ? 'App updated.' : 'App added to this server.');
          load();
          onChanged();
        }}
        onError={onError}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const ServerManagement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState({ open: false, server: null });
  const [openServer, setOpenServer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    serverService.list()
      .then((response) => setData(response.data))
      .catch(() => setError('The servers could not be loaded. Refresh the page to try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Loaded once for the link menus in the app dialog
  useEffect(() => {
    userService.getUsers({ limit: 500 }).then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    whatsappAccountService.getAccounts(1, 500).then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
  }, []);

  const removeServer = async (server) => {
    try {
      await serverService.remove(server._id);
      setSuccess(`${server.ipAddress} removed.`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'The server could not be deleted.');
    }
  };

  const servers = data?.servers || [];
  const totals = data?.totals;
  const current = openServer && servers.find((s) => String(s._id) === String(openServer));

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {current ? (
          <ServerDetail
            server={current}
            customers={customers}
            accounts={accounts}
            onBack={() => setOpenServer(null)}
            onChanged={load}
            onError={setError}
            onSuccess={setSuccess}
          />
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
              <Box>
                <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
                  Servers
                </Typography>
                <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
                  What each server costs us a month, who runs on it, and what those clients bring in.
                  {totals?.month && ` Revenue is ${totals.month}, before GST.`}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, server: null })} sx={buttonSx}>
                Add a server
              </Button>
            </Box>

            {totals && (
              <FigureStrip
                figures={[
                  { label: 'Client hosting', value: money(totals.hostingCost), hint: `${formatCount(totals.hostingServers)} servers, ${formatCount(totals.clients)} clients` },
                  { label: 'Plan revenue', value: money(totals.planRevenue), hint: 'From linked clients only' },
                  { label: 'Other servers', value: money(totals.internalCost), hint: 'Bots, VPN, this portal' },
                  { label: 'Total monthly cost', value: money(totals.monthlyCost) }
                ]}
              />
            )}

            {totals && (
              <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, p: 2, mt: 2, mb: 3, display: 'flex', alignItems: 'baseline', gap: 2 }}>
                <Typography variant="body2" sx={{ color: MUTED }}>Plan revenue less client hosting cost</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: marginColor(totals.margin), fontVariantNumeric: 'tabular-nums' }}>
                  {money(totals.margin)}
                </Typography>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
              {loading && !data ? (
                <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={280} /></Box>
              ) : !servers.length ? (
                <Box sx={{ p: { xs: 3, sm: 5 } }}>
                  <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No servers recorded yet</Typography>
                  <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch' }}>
                    Add each server you rent with its monthly cost, then add the clients that run on it.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        {['Server', 'Provider', 'Clients', 'Monthly cost', 'Cost per client', 'Plan revenue', 'Margin', ''].map((label, i) => (
                          <TableCell
                            key={label || 'menu'}
                            align={['Clients', 'Monthly cost', 'Cost per client', 'Plan revenue', 'Margin'].includes(label) ? 'right' : 'left'}
                            sx={{ color: MUTED, fontWeight: 500, borderColor: RULE, whiteSpace: 'nowrap', pl: i === 0 ? { xs: 2, sm: 3 } : undefined }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {servers.map((server) => (
                        <TableRow
                          key={server._id}
                          hover
                          sx={{ cursor: 'pointer', '& td': { borderColor: RULE, fontVariantNumeric: 'tabular-nums' } }}
                          onClick={() => setOpenServer(String(server._id))}
                        >
                          <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>
                            <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>
                              {server.ipAddress}
                              {server.status === 'retired' && <Chip size="small" label="Retired" sx={{ ml: 1 }} />}
                            </Typography>
                            {server.hostname && <Typography variant="caption" sx={{ color: MUTED }}>{server.hostname}</Typography>}
                          </TableCell>
                          <TableCell sx={{ color: MUTED }}>
                            {server.provider}
                            {(server.purpose && server.purpose !== 'client-hosting') && (
                              <Chip size="small" label={purposeLabel(server.purpose)} sx={{ ml: 1, height: 20 }} />
                            )}
                            {server.spec && <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{server.spec}</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ color: INK }}>{formatCount(server.clients)}</TableCell>
                          <TableCell align="right" sx={{ color: INK }}>{money(server.monthlyCost)}</TableCell>
                          <TableCell align="right" sx={{ color: MUTED }}>{money(server.costPerClient)}</TableCell>
                          <TableCell align="right" sx={{ color: INK }}>
                            {money(server.planRevenue)}
                            {server.clients > server.linkedClients && (
                              <Tooltip title={`${server.clients - server.linkedClients} of these clients are not linked to a customer, so they bring in nothing here`}>
                                <Box component="span" sx={{ color: MUTED, ml: 0.5 }}>*</Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: marginColor(server.margin), fontWeight: 600 }}>{money(server.margin)}</TableCell>
                          <TableCell sx={{ pr: { xs: 2, sm: 3 }, width: 48 }} onClick={(e) => e.stopPropagation()}>
                            <RowMenu
                              label={`Actions for ${server.ipAddress}`}
                              onEdit={() => setDialog({ open: true, server })}
                              onDelete={() => removeServer(server)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}

        <ServerDialog
          open={dialog.open}
          server={dialog.server}
          onClose={() => setDialog({ open: false, server: null })}
          onSaved={(_, editing) => {
            setDialog({ open: false, server: null });
            setSuccess(editing ? 'Server updated.' : 'Server added.');
            load();
          }}
          onError={setError}
        />

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default ServerManagement;
